// src/components/Layout/Navbar.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import styles from './Navbar.module.css';
import {
    FaUserCircle,
    FaDownload,
    FaSpinner,
    FaInfoCircle,
    FaChevronDown,
} from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
// src/services/authApi.js
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'; // Fallback

// Format date function without dependencies
const formatDate = (dateString) => {
  if (!dateString) return 'No date available';
  
  const date = new Date(dateString);
  
  // Format: May 4, 2025 • 2:45 PM
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12;
  hours = hours ? hours : 12; // Convert 0 to 12
  
  return `${month} ${day}, ${year} • ${hours}:${minutes} ${ampm}`;
};

const Navbar = () => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [taskStatus, setTaskStatus] = useState([]);
  const [isTaskLoading, setIsTaskLoading] = useState(false);
  const [showDownloadsDropdown, setShowDownloadsDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const profileIconRef = useRef(null);
  const downloadsIconRef = useRef(null);
  const downloadsDropdownRef = useRef(null);
  const [isAmazonLinked, setIsAmazonLinked] = useState(false);


  // Get Credits Safely for Display
  const creditsValue = (authCtx.user && typeof authCtx.user.credits === 'number')
                         ? authCtx.user.credits.toLocaleString()
                         : '...';

  // Get Tokens Safely for Display
  const tokensValue = (authCtx.user && typeof authCtx.user.tokens === 'number')
                         ? authCtx.user.tokens.toLocaleString()
                         : '...';

  // Fetch task status when dropdown is opened
  const fetchTaskStatus = async () => {
    if (!authCtx?.token) return;
    
    setIsTaskLoading(true);
    try {
      // Fetch bulk generation, deal checker, and listing score tasks
      const [bulkResponse, dealResponse, listingScoreResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/bulk-generation-v2/downloads`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${authCtx.token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/utility_dealtag/deal-checker-history`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${authCtx.token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/listing_score/listing-score-history`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${authCtx.token}` }
        })
      ]);
      
      let allTasks = [];
      
      // Process bulk generation tasks
      if (bulkResponse.ok) {
        const bulkData = await bulkResponse.json();
        if (Array.isArray(bulkData)) {
          const bulkTasks = bulkData.map(task => ({
            ...task,
            task_type: 'bulk_generation',
            display_name: 'Bulk Generation',
            item_count: task.parent_count || 'Unknown',
            item_label: 'parent items',
            task_status: task.task_status || task.status || 'UNKNOWN'
          }));
          allTasks = [...allTasks, ...bulkTasks];
        }
      }
      
      // Process deal checker tasks
      if (dealResponse.ok) {
        const dealData = await dealResponse.json();
        if (dealData.tasks && Array.isArray(dealData.tasks)) {
          const dealTasks = dealData.tasks.map(task => ({
            ...task,
            task_type: 'deal_checker',
            display_name: 'Deal Check',
            item_count: task.total_asins || 0,
            item_label: task.total_asins === 1 ? 'ASIN' : 'ASINs',
            output_file_path: null, // Deal checker doesn't have downloadable files
            output_file_name: null,
            task_status: task.task_status || task.status || 'UNKNOWN'
          }));
          allTasks = [...allTasks, ...dealTasks];
        }
      }
      
      // Process listing score tasks
      if (listingScoreResponse.ok) {
        const listingScoreData = await listingScoreResponse.json();
        if (listingScoreData.success && listingScoreData.tasks && Array.isArray(listingScoreData.tasks)) {
          const listingScoreTasks = listingScoreData.tasks.map(task => ({
            ...task,
            task_type: 'listing_score',
            display_name: 'Listing Score',
            item_count: task.total_asins || (task.asins ? task.asins.length : 0),
            item_label: (task.total_asins || (task.asins ? task.asins.length : 0)) === 1 ? 'ASIN' : 'ASINs',
            output_file_path: null, // Listing scores don't have downloadable files
            output_file_name: null,
            task_status: task.task_status || task.status || 'UNKNOWN',
            request_id: task.task_id // Use task_id as request_id for display
          }));
          allTasks = [...allTasks, ...listingScoreTasks];
        }
      }
      
      // Sort by created_at descending
      allTasks.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
      });
      
      setTaskStatus(allTasks);
    } catch (error) {
      console.error('Error fetching task status:', error);
      setTaskStatus([]);
    } finally {
      setIsTaskLoading(false);
    }
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(prevState => !prevState);
    // Close downloads dropdown if open
    if (showDownloadsDropdown) setShowDownloadsDropdown(false);
  };

  // Toggle downloads dropdown and fetch status
  const toggleDownloadsDropdown = () => {
    const newState = !showDownloadsDropdown;
    setShowDownloadsDropdown(newState);
    
    // Close profile dropdown if open
    if (isProfileDropdownOpen) setIsProfileDropdownOpen(false);
    
    // Fetch status when opening the dropdown
    if (newState) {
      fetchTaskStatus();
    }
  };

  // Credit Modal Handlers
  const openCreditModal = () => {
    setIsCreditModalOpen(true);
  };

  const closeCreditModal = () => {
    setIsCreditModalOpen(false);
  };

  // Get status color based on task status
  const getStatusColor = (status) => {
    if (!status) return '#888888'; // Default gray for unknown
    
    status = status.toUpperCase();
    switch (status) {
      case 'PENDING':
        return '#FFA500'; // Yellow/Orange
      case 'PROCESSING':
        return '#FFA500'; // Yellow/Orange
      case 'PROGRESS':
        return '#FFA500'; // Yellow/Orange
      case 'COMPLETED':
        return '#4CAF50'; // Green
      case 'SUCCESS':
        return '#4CAF50'; // Green
      case 'FAILED':
        return '#F44336'; // Red
      case 'FAILURE':
        return '#F44336'; // Red
      default:
        return '#888888'; // Gray for unknown status
    }
  };

  // Normalize status for display
  const normalizeStatus = (status) => {
    if (!status) return 'UNKNOWN';
    status = status.toUpperCase();
    // Map SUCCESS to COMPLETED for consistency
    if (status === 'SUCCESS') return 'COMPLETED';
    return status;
  };

  // Handle file download
  const handleFileDownload = (url, filename) => {
    if (!url) return;
    
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'download.zip');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Effect to handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle profile dropdown
      if (
        isProfileDropdownOpen &&
        profileIconRef.current && !profileIconRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
      
      // Handle downloads dropdown
      if (
        showDownloadsDropdown &&
        downloadsIconRef.current && !downloadsIconRef.current.contains(event.target) &&
        downloadsDropdownRef.current && !downloadsDropdownRef.current.contains(event.target)
      ) {
        setShowDownloadsDropdown(false);
      }
    };
    
    if (isProfileDropdownOpen || showDownloadsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen, showDownloadsDropdown]);

  // Logout Handler using context
  const handleLogout = () => {
    setIsProfileDropdownOpen(false);
  
    // Get session start time from localStorage
    const sessionStart = localStorage.getItem('sessionStartTime');
    const sessionEnd = Date.now();
    let durationSeconds = null;
  
    if (sessionStart) {
      durationSeconds = Math.round((sessionEnd - parseInt(sessionStart)) / 1000);
    }
  
    // Track logout with PostHog
    if (authCtx.isLoggedIn && authCtx.user) {
      posthog.capture(
        'user_logout',
        { 
          session_end: new Date(sessionEnd).toISOString(),
          session_duration_seconds: durationSeconds
        },
        function(response) {
        }
      );
    }
  
    // Continue with logout
    authCtx.logout();
    navigate('/auth');
  };
  

  // Find the most important status to show in the indicator
  const getHighestPriorityStatus = () => {
    if (!taskStatus || taskStatus.length === 0) return null;
    
    // Priority order: PROCESSING/PENDING > FAILED > COMPLETED
    const processing = taskStatus.find(t => 
      t.task_status === 'PROCESSING' || t.task_status === 'PENDING'
    );
    if (processing) return processing.task_status;
    
    const failed = taskStatus.find(t => t.task_status === 'FAILED');
    if (failed) return failed.task_status;
    
    // Default to first task's status
    return taskStatus[0].task_status;
  };

  // Get user initial for profile icon fallback
  let profileInitial = null;
  if (authCtx.user && authCtx.user.username) {
      profileInitial = String(authCtx.user.username).charAt(0).toUpperCase();
  }

  // Linking to Amazon Seller Account
  const handleAmazonLink = () => {
    if (!isAmazonLinked) {
        // Redirect to Amazon login
        fetch(`${API_URL}/auth/amazon/login`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authCtx.token}`
          }
        })
        .then(res => res.json())
        .then(data => {
          window.location.href = data.auth_url;  // go to Amazon
        });
    }
  };

  // Update amazon linked status when user context changes
  useEffect(() => {
    if (authCtx.user && authCtx.user.amazon_status !== undefined) {
        // Check if amazon_status is connected
        setIsAmazonLinked(authCtx.user.amazon_status === true);
    } else {
        setIsAmazonLinked(false);
    }
  }, [authCtx.user]);

  // Testing SP API
  const testingSPAPI = () => {
    fetch(`${API_URL}/oauth_amazon/spapi/test`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authCtx.token}`
      }
    })
    .then(res => res.json())
    .then(data => {
      // Handle response as needed
    })
    .catch(error => {
      // Handle error
    });
  };

  return (
    <>
      <nav className={styles.navbar}>
        {/* Left side */}
        <div className={styles.navbarLeft}>
          {/* Optional: Title or Search */}
        </div>

        {/* Right side */}
        {/* Optional Tesing Btn */}
        {/* <button 
          className={styles.creditsDisplay}
          onClick={testingSPAPI}
          aria-label="SP API Test Btn"
        >
          <FaStore className={styles.creditsIcon}/>
          <span className={styles.creditsText}>Test Amazon SpApi</span>
        </button> */}

       

        <div className={styles.navbarRight}>
          {/* { isAmazonLinked ? (
            <button 
              className={`${styles.amazonStatusBtn} ${styles.amazonTrueStatus}`}
              // onClick={openCreditModal}
              aria-label="Amazon Seller Account"
            >
              <FaAmazon className={styles.amazonIcon} />
              <span className={styles.amazonText}>Amazon is linked</span>
            </button>
          ) : (
            <button 
              className={`${styles.amazonStatusBtn} ${styles.amazonFalseStatus}`}
              onClick={handleAmazonLink}
              aria-label="Amazon Seller Account"
            >
              <FaAmazon className={styles.amazonIcon} />
              <span className={styles.amazonText}>Link To Amazon</span>
            </button>
          )} */}
          {/* <div className={styles.divider}></div> */}

          
          {/* Credits Display - Now Clickable */}
          {/* <button
            className={styles.creditsDisplay}
            onClick={openCreditModal}
            aria-label="Request Credits"
          >
            <FaCoins className={styles.creditsIcon} />
            <span className={styles.creditsText}>Credits:</span>
            <span className={styles.creditsValue}>{creditsValue}</span>
          </button> */}

          {/* Tokens Display */}
          {/* <button
            className={styles.tokensDisplay}
            aria-label="View Tokens"
          >
            <RiCoinLine className={styles.tokensIcon} />
            <span className={styles.tokensText}>Tokens:</span>
            <span className={styles.tokensValue}>{tokensValue}</span>
          </button> */}

          {/* Download Icon with Task Status Indicator */}
          <div className={styles.downloadContainer}>
            <button
              ref={downloadsIconRef}
              className={styles.iconButton}
              onClick={toggleDownloadsDropdown}
              aria-label="Downloads"
            >
              {isTaskLoading ? (
                <FaSpinner className={styles.spinner} />
              ) : (
                <>
                  <svg width="19" height="25" viewBox="0 0 19 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.78262 2.88284V1.69098C7.78262 1.2679 7.95606 0.883525 8.23615 0.603433L8.23497 0.602261C8.51389 0.323343 8.89944 0.149902 9.32369 0.149902C9.74794 0.149902 10.1335 0.323346 10.4124 0.602261C10.6913 0.881179 10.8648 1.26673 10.8648 1.69098V2.88284C11.5902 3.05394 12.2875 3.3516 12.9238 3.76412C13.9551 4.4333 14.8223 5.406 15.3825 6.64238C16.0072 8.02175 16.0494 9.5675 16.0939 11.1825C16.1408 12.8982 16.1912 14.6971 16.9822 16.3496L16.9845 16.3554H16.9857L17.0174 16.4258C17.3232 16.5418 17.5975 16.7223 17.8248 16.9496C18.2397 17.3645 18.4975 17.9375 18.4975 18.568C18.4975 19.1974 18.2397 19.7692 17.8236 20.1853L17.8248 20.1864C17.41 20.6013 16.8369 20.8591 16.2064 20.8591L13.0212 20.8568C12.9227 21.7135 12.5336 22.4823 11.9559 23.06C11.282 23.7339 10.3515 24.1499 9.32375 24.1499C8.29599 24.1499 7.36548 23.7327 6.69162 23.06C6.11268 22.4811 5.7236 21.7135 5.62633 20.8568H2.4423C1.81414 20.8568 1.24108 20.599 0.825056 20.1841L0.822712 20.1818C0.407855 19.7657 0.150024 19.1938 0.150024 18.5657C0.150024 17.9352 0.407849 17.3621 0.822712 16.9473L0.823884 16.9484C1.05124 16.7211 1.32548 16.5418 1.629 16.4246L1.66181 16.3531C1.6665 16.3437 1.67119 16.3332 1.67588 16.3238C2.45639 14.6772 2.50559 12.8878 2.55365 11.1801C2.59819 9.56635 2.64155 8.02065 3.26501 6.6412C3.82402 5.40599 4.69241 4.43212 5.7237 3.76294C6.36006 3.35042 7.05719 3.05393 7.78262 2.88284ZM9.96473 2.73635V1.69098C9.96473 1.51519 9.89207 1.35464 9.77604 1.23863C9.66002 1.1226 9.49947 1.04994 9.32369 1.04994C9.1479 1.04994 8.98734 1.1226 8.87133 1.23863H8.87016C8.75531 1.35347 8.68382 1.51403 8.68382 1.69098V2.73635C8.89594 2.7176 9.11041 2.70822 9.32486 2.71056C9.53815 2.70822 9.75145 2.71642 9.96473 2.73635ZM15.971 16.2744C15.2913 14.5915 15.2432 12.8618 15.1975 11.2048C15.1553 9.68481 15.1155 8.22927 14.5635 7.00942C14.0783 5.93595 13.3259 5.09333 12.4353 4.5144C11.5036 3.9097 10.4195 3.59327 9.32744 3.605L9.32041 3.60735C8.22818 3.59563 7.14415 3.91204 6.21258 4.51674C5.3219 5.09451 4.5707 5.93829 4.08435 7.01177C3.53239 8.23057 3.49252 9.68611 3.45034 11.2071C3.40463 12.8642 3.35658 14.594 2.67687 16.2767L15.971 16.2744ZM6.53469 20.8565C6.62727 21.4647 6.91321 22.0085 7.32924 22.4234C7.84019 22.9343 8.5457 23.2496 9.32501 23.2496C10.1043 23.2496 10.8098 22.9331 11.3208 22.4234C11.7356 22.0085 12.0228 21.4636 12.1153 20.8565H6.53469ZM2.44222 19.9565H16.2065C16.5885 19.9565 16.9366 19.7994 17.1885 19.5474H17.1897C17.4405 19.2967 17.5964 18.9486 17.5964 18.5654C17.5964 18.1834 17.4393 17.8353 17.1874 17.5833C16.9354 17.3314 16.5874 17.1743 16.2053 17.1743H2.44225C2.05904 17.1743 1.71097 17.3302 1.46019 17.581V17.5822C1.20823 17.8341 1.05118 18.1822 1.05118 18.5642C1.05118 18.9474 1.20705 19.2955 1.45785 19.5463L1.45902 19.5475C1.71098 19.8006 2.05901 19.9565 2.44222 19.9565Z" fill="#A4A4A4" stroke="#A4A4A4" strokeWidth="0.3"/>
                  </svg>

                  {taskStatus && taskStatus.length > 0 && (
                    <span 
                      className={styles.statusIndicator} 
                      style={{ backgroundColor: getStatusColor(getHighestPriorityStatus()) }}
                      title={getHighestPriorityStatus()}
                    ></span>
                  )}
                </>
              )}
            </button>

            {/* Enhanced Download Status Dropdown */}
            {showDownloadsDropdown && (
              <div ref={downloadsDropdownRef} className={styles.downloadsDropdown}>
                <div className={styles.dropdownHeader}>
                  <span>Task History</span>
                </div>
                
                {taskStatus && taskStatus.length > 0 ? (
                  <div className={styles.tasksList}>
                    {taskStatus.map((task, index) => {
                      // Format the date if available
                      const formattedDate = task.created_at ? 
                        formatDate(task.created_at) : 
                        'No date available';
                        
                      // Get item count and label based on task type
                      const itemCount = task.item_count || task?.parent_count || 'Unknown';
                      const itemLabel = task.item_label || 'parent items';
                      const moduleInfo = task.details?.module_type || task.module_info || '';
                      
                      return (
                        <div 
                          key={task.task_id || task.request_id || index} 
                          className={`${styles.taskItem} ${
                            (task.task_status === 'COMPLETED' || task.task_status === 'SUCCESS') ? styles.clickable : ''
                          }`}
                          onClick={() => {
                            if (task.task_type === 'deal_checker' && (task.task_status === 'SUCCESS' || task.task_status === 'COMPLETED')) {
                              // For deal checker, show results in a modal or navigate to results page
                              // You can implement a modal or navigation here
                            } else if (task.task_type === 'listing_score' && (task.task_status === 'SUCCESS' || task.task_status === 'COMPLETED')) {
                              // Navigate to catalogue page to view listing score results
                              navigate('/dashboard/market-places/catalogue');
                            } else if (task.task_status === 'COMPLETED' && task.output_file_path) {
                              handleFileDownload(task.output_file_path, task.output_file_name || `bulk_generator_output_${task.task_id}.zip`);
                            }
                          }}
                        >
                          {/* Status dot */}
                          <div 
                            className={styles.statusDot}
                            style={{ backgroundColor: getStatusColor(task.task_status) }}
                          ></div>
                          
                          {/* File details */}
                          <div className={styles.taskContent}>
                            {/* Task name with request ID if available */}
                            <span className={styles.fileName}>
                              {task.request_id ? 
                                `${task.display_name || 'Task'} #${task.request_id.substring(0, 8)}` : 
                                task.display_name || 'Bulk Generation'}
                            </span>
                            
                            {/* Item count & module info */}
                            <div className={styles.taskMetadata}>
                              <span>{itemCount} {itemLabel}</span>
                              {moduleInfo && (
                                <>
                                  <span>•</span>
                                  <span>{moduleInfo}</span>
                                </>
                              )}
                            </div>
                            
                            {/* Status with date */}
                            <div className={styles.taskStatusRow}>
                              <span 
                                className={styles.taskStatus}
                                style={{ color: getStatusColor(task.task_status) }}
                              >
                                {normalizeStatus(task.task_status)}
                              </span>
                              <span className={styles.taskDate}>{formattedDate}</span>
                            </div>
                          </div>
                          
                          {/* Action icon for completed tasks */}
                          {task.task_status === 'COMPLETED' && task.output_file_path && (
                            <FaDownload className={styles.downloadIcon} />
                          )}
                          {task.task_type === 'deal_checker' && (task.task_status === 'SUCCESS' || task.task_status === 'COMPLETED') && (
                            <FaInfoCircle className={styles.downloadIcon} />
                          )}
                          {task.task_type === 'listing_score' && (task.task_status === 'SUCCESS' || task.task_status === 'COMPLETED') && (
                            <FaInfoCircle className={styles.downloadIcon} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.noTasks}>
                    No active downloads
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.divider}></div>




          {/* Profile Section with Dropdown */}
          <div className={styles.profileDropdownContainer}>
            <button
              ref={profileIconRef}
              className={styles.profileButtonWithInfo}
              onClick={toggleProfileDropdown}
              aria-label="Profile Options"
              aria-haspopup="true"
              aria-expanded={isProfileDropdownOpen}
            >
              <div className={styles.profileAvatar}>
                {authCtx.user?.picture ? (
                  <img 
                    src={authCtx.user.picture} 
                    alt="Profile" 
                    className={styles.profileImage}
                  />
                ) : profileInitial ? (
                  <span className={styles.profileInitial}>{profileInitial}</span>
                ) : (
                  <FaUserCircle className={styles.profileIcon} />
                )}
              </div>
              <div className={styles.profileInfo}>
                <span className={styles.profileName}>
                  {authCtx.user?.name || authCtx.user?.username || 'User'}
                </span>
                <span className={styles.profileCompany}>
                  {authCtx.user?.companyname || 'Company'}
                </span>
              </div>
              <FaChevronDown className={styles.profileChevron} />
            </button>

            {isProfileDropdownOpen && (
              <div ref={dropdownRef} className={styles.profileDropdown} role="menu">
                {authCtx.user && (
                    <div className={styles.dropdownHeader}>
                        <span className={styles.dropdownUsername}>{authCtx.user.username || 'User'}</span>
                        <span className={styles.dropdownEmail}>{authCtx.user.email || 'No Email'}</span>
                    </div>
                )}
                <div className={styles.dropdownDivider} />
                <Link to="/dashboard/profile" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)} role="menuitem">
                  <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.dropdownIcon}>
                    <path fillRule="evenodd" clipRule="evenodd" d="M8 0C10.3769 0 12.3077 1.94876 12.3077 4.34783C12.3077 6.74689 10.3769 8.69565 8 8.69565C5.62308 8.69565 3.69231 6.74689 3.69231 4.34783C3.69231 1.94876 5.62308 0 8 0ZM8 1.24224C6.30154 1.24224 4.92308 2.63354 4.92308 4.34783C4.92308 6.06211 6.30154 7.45342 8 7.45342C9.69846 7.45342 11.0769 6.06211 11.0769 4.34783C11.0769 2.63354 9.69846 1.24224 8 1.24224ZM11.6923 9.93789C14.0715 9.93789 16 11.8843 16 14.2857V15.5279C16 16.0225 15.8054 16.4961 15.4592 16.8455C15.1131 17.1949 14.6438 17.3913 14.1538 17.3913H1.84615C1.35615 17.3913 0.886922 17.1949 0.540771 16.8455C0.194619 16.4961 0 16.0225 0 15.5279V14.2857C0 11.8843 1.92847 9.93789 4.30769 9.93789H11.6923ZM11.6923 11.1801H4.30769C2.60846 11.1801 1.23077 12.5707 1.23077 14.2857V15.5279C1.23077 15.6925 1.29538 15.8509 1.41077 15.9674C1.52615 16.0838 1.68307 16.1491 1.84615 16.1491H14.1538C14.3169 16.1491 14.4738 16.0838 14.5892 15.9674C14.7046 15.8509 14.7692 15.6925 14.7692 15.5279V14.2857C14.7692 12.5707 13.3915 11.1801 11.6923 11.1801Z" fill="#898989"/>
                  </svg>
                  <span>Your Profile</span>
                </Link>
                <Link to="/dashboard/plans" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)} role="menuitem">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.dropdownIcon}>
                    <path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 14.5C4.41015 14.5 1.5 11.5899 1.5 8C1.5 4.41015 4.41015 1.5 8 1.5C11.5899 1.5 14.5 4.41015 14.5 8C14.5 11.5899 11.5899 14.5 8 14.5Z" fill="#898989"/>
                    <path d="M8.8 4.4C8.8 3.95817 8.44183 3.6 8 3.6C7.55817 3.6 7.2 3.95817 7.2 4.4V4.8C6.09543 4.8 5.2 5.69543 5.2 6.8C5.2 7.90457 6.09543 8.8 7.2 8.8H8.8C9.02091 8.8 9.2 8.97909 9.2 9.2C9.2 9.42091 9.02091 9.6 8.8 9.6H5.6C5.15817 9.6 4.8 9.95817 4.8 10.4C4.8 10.8418 5.15817 11.2 5.6 11.2H7.2V11.6C7.2 12.0418 7.55817 12.4 8 12.4C8.44183 12.4 8.8 12.0418 8.8 11.6V11.2C9.90457 11.2 10.8 10.3046 10.8 9.2C10.8 8.09543 9.90457 7.2 8.8 7.2H7.2C6.97909 7.2 6.8 7.02091 6.8 6.8C6.8 6.57909 6.97909 6.4 7.2 6.4H10.4C10.8418 6.4 11.2 6.04183 11.2 5.6C11.2 5.15817 10.8418 4.8 10.4 4.8H8.8V4.4Z" fill="#898989"/>
                  </svg>
                  <span>Buy Credits</span>
                </Link>
                <Link to="/dashboard/plans#billing-history" className={styles.dropdownItem} onClick={() => setIsProfileDropdownOpen(false)} role="menuitem">
                  <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.dropdownIcon}>
                    <path d="M13 0H3C1.34315 0 0 1.34315 0 3V15C0 16.6569 1.34315 18 3 18H13C14.6569 18 16 16.6569 16 15V3C16 1.34315 14.6569 0 13 0ZM14.5 15C14.5 15.8284 13.8284 16.5 13 16.5H3C2.17157 16.5 1.5 15.8284 1.5 15V3C1.5 2.17157 2.17157 1.5 3 1.5H13C13.8284 1.5 14.5 2.17157 14.5 3V15ZM4 5H12C12.4142 5 12.75 4.66421 12.75 4.25C12.75 3.83579 12.4142 3.5 12 3.5H4C3.58579 3.5 3.25 3.83579 3.25 4.25C3.25 4.66421 3.58579 5 4 5ZM4 9.5H12C12.4142 9.5 12.75 9.16421 12.75 8.75C12.75 8.33579 12.4142 8 12 8H4C3.58579 8 3.25 8.33579 3.25 8.75C3.25 9.16421 3.58579 9.5 4 9.5ZM4 14H8C8.41421 14 8.75 13.6642 8.75 13.25C8.75 12.8358 8.41421 12.5 8 12.5H4C3.58579 12.5 3.25 12.8358 3.25 13.25C3.25 13.6642 3.58579 14 4 14Z" fill="#898989"/>
                  </svg>
                  <span>View Invoices</span>
                </Link>
                <div className={styles.dropdownDivider} />
                <button onClick={handleLogout} className={styles.dropdownItem} role="menuitem">
                  <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.dropdownIcon}>
                    <path fillRule="evenodd" clipRule="evenodd" d="M14.4135 8.11511C14.623 8.33363 14.6251 8.66781 14.4135 8.88561L11.4404 11.9059C10.941 12.4132 10.1826 11.6427 10.6819 11.1354L12.7395 9.04523H4.27215C3.56607 9.04523 3.56607 7.95546 4.27215 7.95546H12.7395L10.6819 5.86529C10.1826 5.358 10.941 4.5875 11.4404 5.09479L14.4135 8.11511ZM8.8619 13.7094V15.4186C8.8619 15.6882 8.64259 15.9103 8.37791 15.9103L1.55672 15.911C1.28924 15.911 1.07274 15.691 1.07274 15.4193V1.58143C1.07274 1.31182 1.29204 1.08977 1.55672 1.08977H8.37791C8.6461 1.08977 8.8619 1.30899 8.8619 1.58143V3.29059C8.8619 4.00787 9.93464 4.00787 9.93464 3.29059V1.58143C9.93464 0.713743 9.23205 0 8.37791 0H1.55672C0.699086 0 0 0.710201 0 1.58143V15.4186C0 16.287 0.701893 17 1.55672 17H8.37791C9.23555 17 9.93464 16.2898 9.93464 15.4186V13.7094C9.93464 12.9921 8.8619 12.9921 8.8619 13.7094Z" fill="#505050"/>
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

    </>
  );
};

export default Navbar;
